import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import AdminHeader from '../../../components/ui/AdminHeader';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { getAllUsers, createAdminUser, updateUserRole, toggleUserActiveStatus, deleteUserAccount } from '../../../services/adminService';

const AdminUsersPage = () => {
  const [isNavCollapsed, setIsNavCollapsed] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'consumer',
    phone: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  // Check if user is admin
  useEffect(() => {
    if (userProfile && userProfile?.role !== 'admin') {
      navigate('/');
      return;
    }
  }, [userProfile, navigate]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (selectedRole) filters.role = selectedRole;
      if (searchTerm) filters.search = searchTerm;
      
      const data = await getAllUsers(filters);
      setUsers(data || []);
    } catch (error) {
      setError(`Failed to load users: ${error?.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [selectedRole, searchTerm]);

  const handleCreateUser = async (e) => {
    e?.preventDefault();
    try {
      setError('');
      await createAdminUser(createFormData);
      setSuccess('User created successfully');
      setShowCreateModal(false);
      setCreateFormData({
        email: '',
        password: '',
        full_name: '',
        role: 'consumer',
        phone: ''
      });
      loadUsers();
    } catch (error) {
      setError(`Failed to create user: ${error?.message}`);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      setError('');
      await updateUserRole(userId, newRole);
      setSuccess('User role updated successfully');
      loadUsers();
    } catch (error) {
      setError(`Failed to update role: ${error?.message}`);
    }
  };

  const handleToggleActiveStatus = async (userId, currentStatus) => {
    try {
      setError('');
      await toggleUserActiveStatus(userId, !currentStatus);
      setSuccess(`User ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      loadUsers();
    } catch (error) {
      setError(`Failed to update status: ${error?.message}`);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    
    try {
      setError('');
      await deleteUserAccount(userId);
      setSuccess('User deleted successfully');
      loadUsers();
    } catch (error) {
      setError(`Failed to delete user: ${error?.message}`);
    }
  };

  const roleOptions = [
    { value: '', label: 'All Roles' },
    { value: 'admin', label: 'Admin' },
    { value: 'manager', label: 'Manager' },
    { value: 'consumer', label: 'Consumer' }
  ];

  const userRoleOptions = [
    { value: 'admin', label: 'Admin' },
    { value: 'manager', label: 'Manager' },
    { value: 'consumer', label: 'Consumer' }
  ];

  if (userProfile?.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader
        title="User Management"
        description="Manage platform users, roles, and permissions"
        user={{
          name: userProfile?.full_name || "Admin User",
          role: "Platform Administrator"
        }}
      >
        <Button
          onClick={() => setShowCreateModal(true)}
          iconName="UserPlus"
        >
          Create User
        </Button>
      </AdminHeader>

      <main className="p-6">
        <div className="max-w-7xl mx-auto">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}
          
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
              {success}
            </div>
          )}

          {/* Section Header */}
          <div className="mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Users" size={20} color="white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">User Management</h2>
                <p className="text-sm text-muted-foreground">Manage platform users, roles, and permissions</p>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-card border border-border rounded-lg p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                iconName="Search"
              />
              <Select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e?.target?.value)}
                options={roleOptions}
              />
              <div className="text-sm text-muted-foreground flex items-center">
                Total Users: {users?.length || 0}
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">User</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Role</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Status</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Joined</th>
                    <th className="text-right px-4 py-3 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="text-center py-8 text-muted-foreground">
                        Loading users...
                      </td>
                    </tr>
                  ) : users?.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center py-8 text-muted-foreground">
                        No users found
                      </td>
                    </tr>
                  ) : (
                    users?.map((user) => (
                      <tr key={user?.id} className="border-t border-border hover:bg-muted/50">
                        <td className="px-4 py-3">
                          <div>
                            <div className="font-medium text-foreground">{user?.full_name}</div>
                            <div className="text-sm text-muted-foreground">{user?.email}</div>
                            {user?.phone && (
                              <div className="text-sm text-muted-foreground">{user?.phone}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Select
                            value={user?.role}
                            onChange={(e) => handleRoleChange(user?.id, e?.target?.value)}
                            options={userRoleOptions}
                            size="sm"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            user?.is_active 
                              ? 'bg-green-100 text-green-800' :'bg-red-100 text-red-800'
                          }`}>
                            {user?.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {new Date(user?.created_at)?.toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleToggleActiveStatus(user?.id, user?.is_active)}
                            >
                              {user?.is_active ? 'Deactivate' : 'Activate'}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteUser(user?.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-foreground mb-4">Create New User</h3>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <Input
                label="Full Name"
                required
                value={createFormData?.full_name}
                onChange={(e) => setCreateFormData({...createFormData, full_name: e?.target?.value})}
              />
              <Input
                label="Email"
                type="email"
                required
                value={createFormData?.email}
                onChange={(e) => setCreateFormData({...createFormData, email: e?.target?.value})}
              />
              <Input
                label="Password"
                type="password"
                required
                value={createFormData?.password}
                onChange={(e) => setCreateFormData({...createFormData, password: e?.target?.value})}
              />
              <Input
                label="Phone (Optional)"
                type="tel"
                value={createFormData?.phone}
                onChange={(e) => setCreateFormData({...createFormData, phone: e?.target?.value})}
              />
              <Select
                label="Role"
                value={createFormData?.role}
                onChange={(e) => setCreateFormData({...createFormData, role: e?.target?.value})}
                options={userRoleOptions}
              />
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  Create User
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;